import React, { useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import { practiceAreasService } from '../../../services/practiceAreasService';
import { storageService } from '../../../services/storageService';

const PracticeAreasManager = () => {
	const [practiceAreas, setPracticeAreas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingArea, setEditingArea] = useState(null);
	const [showAddForm, setShowAddForm] = useState(false);
	const [formData, setFormData] = useState({
		title: '',
		slug: '',
		category: 'General',
		icon: '',
		tagline: '',
		description: '',
		full_description: '',
		image_url: '',
		specialties: [],
		services: [],
		is_featured: false,
		is_active: true,
		display_order: 0,
	});

	useEffect(() => {
		loadPracticeAreas();
	}, []);

	const loadPracticeAreas = async () => {
		try {
			setLoading(true);
			const { data, error } = await practiceAreasService.getAllPracticeAreas();
			if (error) {
				console.error('Error loading practice areas:', error);
				setPracticeAreas([]);
			} else {
				setPracticeAreas(data || []);
			}
		} catch (error) {
			console.error('Error loading practice areas:', error);
			setPracticeAreas([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (editingArea) {
				// Update existing practice area
				console.log('Updating practice area with data:', formData);
				console.log('Practice area ID:', editingArea.id);
				const result = await practiceAreasService.updatePracticeArea(editingArea.id, formData);
				console.log('Update result:', result);

				if (result.error) {
					console.error('Error updating practice area:', result.error);
					console.error('Error details:', JSON.stringify(result.error, null, 2));
					alert(
						'Failed to update practice area: ' +
							(result.error.message || result.error || 'Unknown error')
					);
					return;
				}
				alert('Practice area updated successfully!');
			} else {
				// Create new practice area
				console.log('Creating practice area with data:', formData);
				const { error } = await practiceAreasService.createPracticeArea(formData);
				if (error) {
					console.error('Error creating practice area:', error);
					alert('Failed to create practice area: ' + error.message);
					return;
				}
				alert('Practice area created successfully!');
			}

			resetForm();
			loadPracticeAreas();
		} catch (error) {
			console.error('Error saving practice area:', error);
			alert('Error saving practice area: ' + error.message);
		}
	};

	const handleEdit = (area) => {
		setEditingArea(area);
		setFormData({
			...area,
			// Ensure services and specialties are properly formatted for the form
			specialties: Array.isArray(area.specialties) ? area.specialties : [],
			services: Array.isArray(area.services) ? area.services : [],
		});
		setShowAddForm(true);
	};

	const handleDelete = async (areaId) => {
		if (!confirm('Are you sure you want to delete this practice area?')) {
			return;
		}

		try {
			const { error } = await practiceAreasService.deletePracticeArea(areaId);
			if (error) {
				console.error('Error deleting practice area:', error);
				alert('Failed to delete practice area. Please try again.');
				return;
			}
			alert('Practice area deleted successfully!');
			loadPracticeAreas();
		} catch (error) {
			console.error('Error deleting practice area:', error);
			alert('Error deleting practice area: ' + error.message);
		}
	};

	const resetForm = () => {
		setFormData({
			title: '',
			slug: '',
			category: 'General',
			icon: '',
			tagline: '',
			description: '',
			full_description: '',
			image_url: '',
			specialties: [],
			services: [],
			is_featured: false,
			is_active: true,
			display_order: 0,
		});
		setEditingArea(null);
		setShowAddForm(false);
	};

	const handleImageUpload = async (file) => {
		try {
			const { data, error } = await storageService.uploadImage(file, 'images', 'practice-areas/');

			if (error) {
				console.error('Upload error:', error);
				alert('Upload failed: ' + error.message);
				return;
			}

			if (data) {
				setFormData((prev) => ({ ...prev, image_url: data.publicUrl }));
				alert('Image uploaded successfully!');
			}
		} catch (error) {
			console.error('Upload error:', error);
			alert('Upload failed: ' + error.message);
		}
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex items-center justify-between'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Practice Areas Manager</h1>
						<p className='text-gray-600'>Manage legal practice areas and specializations</p>
					</div>
					<button
						onClick={() => setShowAddForm(true)}
						className='bg-[#800020] text-white px-4 py-2 rounded-md hover:bg-[#6b001a] transition-colors'
					>
						<Icon name='Plus' className='h-4 w-4 inline mr-2' />
						Add New Practice Area
					</button>
				</div>
			</div>

			{/* Add/Edit Form */}
			{showAddForm && (
				<div className='bg-white shadow rounded-lg p-6 border-2 border-[#800020]'>
					<div className='flex items-center justify-between mb-4'>
						<h2 className='text-lg font-semibold text-gray-900'>
							{editingArea ? (
								<span className='flex items-center'>
									<Icon name='Edit' className='h-5 w-5 text-[#800020] mr-2' />
									Edit Practice Area: {editingArea.title}
								</span>
							) : (
								<span className='flex items-center'>
									<Icon name='Plus' className='h-5 w-5 text-[#800020] mr-2' />
									Add New Practice Area
								</span>
							)}
						</h2>
						<button
							onClick={resetForm}
							className='text-gray-500 hover:text-gray-700 transition-colors'
							title='Cancel'
						>
							<Icon name='X' className='h-5 w-5' />
						</button>
					</div>

					<form onSubmit={handleSubmit} className='space-y-4'>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Title *</label>
								<input
									type='text'
									value={formData.title}
									onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Slug *</label>
								<input
									type='text'
									value={formData.slug}
									onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='practice-area-slug'
									required
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Short Description
							</label>
							<textarea
								value={formData.description}
								onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
								rows={2}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Brief description for cards...'
							/>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Full Description
							</label>
							<textarea
								value={formData.full_description}
								onChange={(e) =>
									setFormData((prev) => ({ ...prev, full_description: e.target.value }))
								}
								rows={6}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Detailed description with bullet points...'
							/>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Tagline</label>
								<input
									type='text'
									value={formData.tagline}
									onChange={(e) => setFormData((prev) => ({ ...prev, tagline: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='Expert legal services...'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Icon Name</label>
								<input
									type='text'
									value={formData.icon}
									onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='Briefcase, Scale, etc.'
								/>
							</div>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Image URL</label>
								<input
									type='text'
									value={formData.image_url}
									onChange={(e) => setFormData((prev) => ({ ...prev, image_url: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='https://your-project.supabase.co/storage/v1/object/public/images/...'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Upload Image</label>
								<input
									type='file'
									accept='image/*'
									onChange={(e) => {
										if (e.target.files[0]) {
											handleImageUpload(e.target.files[0]);
										}
									}}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Specialties (one per line)
							</label>
							<textarea
								value={Array.isArray(formData.specialties) ? formData.specialties.join('\n') : ''}
								onChange={(e) => {
									const specialties = e.target.value.split('\n').filter((s) => s.trim());
									setFormData((prev) => ({ ...prev, specialties }));
								}}
								rows={3}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Contract Law&#10;Mergers & Acquisitions&#10;Corporate Governance'
							/>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Services (one per line)
							</label>
							<textarea
								value={
									Array.isArray(formData.services)
										? formData.services
												.map((service) =>
													typeof service === 'string' ? service : service.title || service
												)
												.join('\n')
										: ''
								}
								onChange={(e) => {
									const services = e.target.value
										.split('\n')
										.filter((s) => s.trim())
										.map((title) => ({ title, description: '' }));
									setFormData((prev) => ({ ...prev, services }));
								}}
								rows={3}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Legal Consultation&#10;Contract Review&#10;Due Diligence&#10;Compliance Audits'
							/>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Category</label>
								<select
									value={formData.category}
									onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								>
									<option value='General'>General</option>
									<option value='Specialized'>Specialized</option>
									<option value='Corporate'>Corporate</option>
									<option value='Litigation'>Litigation</option>
									<option value='Transactional'>Transactional</option>
								</select>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Display Order
								</label>
								<input
									type='number'
									value={formData.display_order}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, display_order: parseInt(e.target.value) }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div className='flex items-center'>
								<input
									type='checkbox'
									id='is_featured'
									checked={formData.is_featured}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))
									}
									className='h-4 w-4 text-[#800020] focus:ring-[#800020] border-gray-300 rounded'
								/>
								<label htmlFor='is_featured' className='ml-2 block text-sm text-gray-900'>
									Featured
								</label>
							</div>

							<div className='flex items-center'>
								<input
									type='checkbox'
									id='is_active'
									checked={formData.is_active}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
									}
									className='h-4 w-4 text-[#800020] focus:ring-[#800020] border-gray-300 rounded'
								/>
								<label htmlFor='is_active' className='ml-2 block text-sm text-gray-900'>
									Active
								</label>
							</div>
						</div>

						<div className='flex justify-end space-x-3'>
							<button
								type='button'
								onClick={resetForm}
								className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center'
							>
								<Icon name='X' className='h-4 w-4 mr-2' />
								Cancel
							</button>
							<button
								type='submit'
								className='px-4 py-2 bg-[#800020] text-white rounded-md hover:bg-[#6b001a] transition-colors flex items-center'
							>
								<Icon name={editingArea ? 'Save' : 'Plus'} className='h-4 w-4 mr-2' />
								{editingArea ? 'Update Practice Area' : 'Create Practice Area'}
							</button>
						</div>
					</form>
				</div>
			)}

			{/* Practice Areas List */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Current Practice Areas</h2>

				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading practice areas...</span>
					</div>
				) : !practiceAreas || !Array.isArray(practiceAreas) || practiceAreas.length === 0 ? (
					<div className='text-center py-8 text-gray-500'>
						<Icon name='Briefcase' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
						<p>No practice areas found</p>
					</div>
				) : (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
						{practiceAreas.map((area) => (
							<div key={area.id} className='border border-gray-200 rounded-lg p-4'>
								<div className='flex items-start justify-between mb-3'>
									<div className='w-12 h-12 bg-gray-100 rounded overflow-hidden'>
										{area.image_url ? (
											<img
												src={area.image_url}
												alt={area.title}
												className='w-full h-full object-cover'
											/>
										) : (
											<div className='w-full h-full flex items-center justify-center text-gray-400'>
												<Icon name='Briefcase' className='h-6 w-6' />
											</div>
										)}
									</div>
									<div className='flex space-x-2'>
										<button
											onClick={() => handleEdit(area)}
											className='text-[#800020] hover:text-[#6b001a] transition-colors'
										>
											<Icon name='Edit' className='h-4 w-4' />
										</button>
										<button
											onClick={() => handleDelete(area.id)}
											className='text-red-600 hover:text-red-700 transition-colors'
										>
											<Icon name='Trash2' className='h-4 w-4' />
										</button>
									</div>
								</div>
								<h3 className='font-medium text-gray-900 mb-1'>{area.title}</h3>
								<p className='text-sm text-gray-600 mb-2'>{area.description}</p>
								<div className='flex items-center justify-between'>
									<span className='text-xs text-gray-500'>Order: {area.order_index}</span>
									<span
										className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
											area.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
										}`}
									>
										{area.is_active ? 'Active' : 'Inactive'}
									</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default PracticeAreasManager;
